/**
 * Organization router — self-serve org management.
 *
 * Roles:
 *   - owner       — can do everything, including transferring/promoting other owners
 *                   and removing the org (out of scope for v1).
 *   - admin       — can invite + remove members, can set roles up to admin.
 *   - compliance  — read-only org context; required for the NIL Go +
 *                   eligibility dashboards (US-031.4 / US-033.5).
 *   - member      — vanilla member, no admin powers.
 *
 * Why not better-auth's `organization()` client directly? It supports
 * owner/admin/member out of the box but adding `compliance` requires
 * custom access-control. Wrapping here also gives us a single place for
 * audit log + a stable oRPC contract that the UI can typecheck against.
 *
 * @package @app/api
 */

import { ORPCError } from '@orpc/server';
import { z } from 'zod';
import { writeAuditLog } from '../lib/audit';
// Invite core + role helpers extracted to org-invite.ts (agent epic PR 3)
// so agency.inviteProspect runs the identical invite path. Behavior when
// called from this router is unchanged.
import {
  ORG_ROLES,
  STAFF_ROLES,
  autoSkipNcaaOnboardingForStaff,
  callerRole,
  inviteMemberCore,
  requireRole,
} from '../lib/org-invite';
import type { OrgRole } from '../lib/org-invite';
import { protectedProcedure } from '../middleware/auth';

const roleEnum = z.enum(ORG_ROLES);

const ORG_TYPES = ['university', 'collective', 'agency', 'brand', 'other'] as const;
const orgTypeEnum = z.enum(ORG_TYPES);

// biome-ignore lint/suspicious/noExplicitAny: prisma client type
type Db = any;

/** Convert a name to a URL-safe slug. Best-effort — collisions caught
 * at insert time by the unique constraint. */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export const organizationRouter = {
  /** Orgs the caller belongs to, plus the caller's role in each. The
   * shape mirrors what `/settings/organizations` renders. */
  myOrgs: protectedProcedure.handler(async ({ context }) => {
    const { db, session } = context;
    const rows = await db.member.findMany({
      where: { userId: session.user.id },
      include: {
        organization: {
          select: { id: true, name: true, slug: true, type: true, createdAt: true },
        },
      },
      orderBy: { createdAt: 'desc' },
    });
    return rows.map((m: any) => ({
      orgId: m.organization.id,
      name: m.organization.name,
      slug: m.organization.slug,
      type: m.organization.type,
      role: m.role as OrgRole,
      createdAt: m.organization.createdAt.toISOString(),
    }));
  }),

  /** Create a new org. The caller becomes its owner. Slug defaults to a
   * slugified name; collisions surface as a friendly error rather than a
   * prisma constraint violation. */
  create: protectedProcedure
    .input(
      z.object({
        name: z.string().min(2).max(80),
        slug: z.string().min(2).max(60).optional(),
        type: orgTypeEnum.default('university'),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const desiredSlug = input.slug?.trim() || slugify(input.name);
      if (!desiredSlug) {
        throw new ORPCError('BAD_REQUEST', { message: 'Slug is empty after normalization.' });
      }

      // Each step throws with a per-step label so a generic "Internal
      // server error" gets replaced with a specific message we can act on.
      let step: string = 'collision-check';
      try {
        // Use the raw (un-enhanced) prisma client for these writes.
        // ZenStack policies on `Organization` allow create only when
        // `auth().role == 'admin'` (global admin), and `Member.create`
        // is gated on already being an owner/admin of the target org —
        // a chicken-and-egg lock that blocks any self-serve flow. Our
        // protectedProcedure already authenticated the caller and the
        // handler enforces role checks in code, so dropping the row-
        // level policy here is the right boundary.
        const { prisma: rawPrisma, createId } = await import('@app/db');

        const collision = await rawPrisma.organization.findUnique({ where: { slug: desiredSlug } });
        if (collision) {
          throw new ORPCError('CONFLICT', {
            message: `An org with slug "${desiredSlug}" already exists. Try a different name.`,
          });
        }

        const orgId = createId();

        step = 'org-create';
        await rawPrisma.organization.create({
          data: {
            id: orgId,
            name: input.name.trim(),
            slug: desiredSlug,
            type: input.type,
          },
        });

        step = 'member-create';
        await rawPrisma.member.create({
          data: {
            id: createId(),
            userId: session.user.id,
            organizationId: orgId,
            role: 'owner',
          },
        });

        step = 'skip-ncaa';
        // Creator is staff (owner), so suppress the NCAA athlete onboarding gate.
        await rawPrisma.user.updateMany({
          where: { id: session.user.id, onboardingNcaaIdSkippedAt: null },
          data: { onboardingNcaaIdSkippedAt: new Date() },
        });

        step = 'audit';
        await writeAuditLog({
          action: 'organization.created',
          category: 'compliance',
          actor: {
            id: session.user.id,
            name: session.user.name ?? null,
            email: session.user.email ?? null,
            role: session.user.role ?? null,
          },
          target: { type: 'organization', id: orgId, name: input.name },
          context: { slug: desiredSlug, orgType: input.type },
        });

        return { orgId, name: input.name, slug: desiredSlug };
      } catch (err) {
        if (err instanceof ORPCError) throw err;
        const msg = err instanceof Error ? err.message : String(err);
        console.error(`[organization.create] failed at step=${step}:`, msg, err);
        throw new ORPCError('INTERNAL_SERVER_ERROR', {
          message: `Create failed at step "${step}": ${msg}`,
        });
      }
    }),

  /** Members of an org. Caller must already be a member of the org. */
  listMembers: protectedProcedure
    .input(z.object({ orgId: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const role = await callerRole(db, session.user.id, input.orgId);
      requireRole(role, ORG_ROLES);
      const members = await db.member.findMany({
        where: { organizationId: input.orgId },
        include: {
          user: { select: { id: true, name: true, email: true, image: true } },
        },
        orderBy: { createdAt: 'asc' },
      });
      return {
        callerRole: role as OrgRole,
        members: members.map((m: any) => ({
          memberId: m.id,
          userId: m.user.id,
          name: m.user.name,
          email: m.user.email,
          image: m.user.image,
          role: m.role as OrgRole,
          joinedAt: m.createdAt.toISOString(),
        })),
      };
    }),

  /** Add a member by email. Two paths:
   *   - If the email already has a NilBoard account → add a Member row
   *     directly + send the in-app + email notification.
   *   - If the email is brand new → create a stub User row, then add the
   *     Member row, then send a custom invitation email pointing them at
   *     /auth/login. better-auth's magic-link verify path calls
   *     `findUserByEmail` and re-uses the existing user (verified by
   *     reading the plugin source + DB probe), so on their first sign-in
   *     they land directly in the org as the staff role we set.
   *
   * Owners + admins can add; only owners can grant the `owner` role.
   */
  invite: protectedProcedure
    .input(z.object({ orgId: z.string(), email: z.string().email(), role: roleEnum }))
    .handler(async ({ context, input }) => inviteMemberCore(context, input)),

  /** Change a member's role. Only owners can do this. Cannot demote the
   * last remaining owner — every org keeps at least one. */
  updateMemberRole: protectedProcedure
    .input(z.object({ orgId: z.string(), userId: z.string(), role: roleEnum }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const callerR = await callerRole(db, session.user.id, input.orgId);
      requireRole(callerR, ['owner']);

      const target = await db.member.findFirst({
        where: { userId: input.userId, organizationId: input.orgId },
      });
      if (!target) throw new ORPCError('NOT_FOUND', { message: 'Member not found.' });

      if (target.role === 'owner' && input.role !== 'owner') {
        const ownerCount = await db.member.count({
          where: { organizationId: input.orgId, role: 'owner' },
        });
        if (ownerCount <= 1) {
          throw new ORPCError('BAD_REQUEST', {
            message: 'Cannot demote the last owner. Promote another member to owner first.',
          });
        }
      }

      const { prisma: rawPrisma } = await import('@app/db');
      await rawPrisma.member.update({ where: { id: target.id }, data: { role: input.role } });

      // Promotion to staff role suppresses the NCAA athlete onboarding
      // gate (same as `invite` does on first add).
      const becameStaff =
        !(STAFF_ROLES as readonly string[]).includes(target.role) &&
        (STAFF_ROLES as readonly string[]).includes(input.role);
      if (becameStaff) {
        await autoSkipNcaaOnboardingForStaff(rawPrisma, input.userId);
      }

      await writeAuditLog({
        action: 'organization.member_role_changed',
        category: 'compliance',
        actor: {
          id: session.user.id,
          name: session.user.name ?? null,
          email: session.user.email ?? null,
          role: session.user.role ?? null,
        },
        target: { type: 'user', id: input.userId, name: null },
        context: { orgId: input.orgId, previousRole: target.role, newRole: input.role },
      });
      return { ok: true };
    }),

  /** Remove a member. Owners + admins can remove; admins cannot remove
   * owners. Same last-owner protection as updateMemberRole. */
  removeMember: protectedProcedure
    .input(z.object({ orgId: z.string(), userId: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const callerR = await callerRole(db, session.user.id, input.orgId);
      requireRole(callerR, ['owner', 'admin']);

      const target = await db.member.findFirst({
        where: { userId: input.userId, organizationId: input.orgId },
      });
      if (!target) throw new ORPCError('NOT_FOUND', { message: 'Member not found.' });

      if (target.role === 'owner') {
        if (callerR !== 'owner') {
          throw new ORPCError('FORBIDDEN', { message: 'Only owners can remove other owners.' });
        }
        const ownerCount = await db.member.count({
          where: { organizationId: input.orgId, role: 'owner' },
        });
        if (ownerCount <= 1) {
          throw new ORPCError('BAD_REQUEST', {
            message: 'Cannot remove the last owner. Promote another member to owner first.',
          });
        }
      }

      const { prisma: rawPrisma } = await import('@app/db');
      await rawPrisma.member.delete({ where: { id: target.id } });
      await writeAuditLog({
        action: 'organization.member_removed',
        category: 'compliance',
        actor: {
          id: session.user.id,
          name: session.user.name ?? null,
          email: session.user.email ?? null,
          role: session.user.role ?? null,
        },
        target: { type: 'user', id: input.userId, name: null },
        context: { orgId: input.orgId, previousRole: target.role },
      });
      return { ok: true };
    }),

  /** Leave an org. Any member can leave; the last owner cannot. */
  leave: protectedProcedure
    .input(z.object({ orgId: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const target = await db.member.findFirst({
        where: { userId: session.user.id, organizationId: input.orgId },
      });
      if (!target) throw new ORPCError('NOT_FOUND', { message: 'You are not a member of this org.' });
      if (target.role === 'owner') {
        const ownerCount = await db.member.count({
          where: { organizationId: input.orgId, role: 'owner' },
        });
        if (ownerCount <= 1) {
          throw new ORPCError('BAD_REQUEST', {
            message: 'Promote another member to owner before leaving.',
          });
        }
      }
      const { prisma: rawPrisma } = await import('@app/db');
      await rawPrisma.member.delete({ where: { id: target.id } });
      await writeAuditLog({
        action: 'organization.member_left',
        category: 'compliance',
        actor: {
          id: session.user.id,
          name: session.user.name ?? null,
          email: session.user.email ?? null,
          role: session.user.role ?? null,
        },
        target: { type: 'organization', id: input.orgId, name: null },
        context: { previousRole: target.role },
      });
      return { ok: true };
    }),
};
